import EscapeManager from '../../../js/Managers/EscapeManager.js';

class SearchForm extends HTMLElement {
  escapeManagerID = `searchResults` + Math.random().toString(36).substring(2, 15);
  input = null;
  resultsContainer = null;
  listElement = null;
  searchEndPoint = null;
  buttonIcon = null;
  hasResults = false;

  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('.search-form__results');
    this.listElement = this.querySelector('.search-form__results__list');
    this.buttonIcon = this.querySelector('.button__icon .faicon');

    if (this.hasAttribute('data-endpoint')) {
      this.searchEndPoint = this.getAttribute('data-endpoint');
    }

    this._onInput = this.onInput.bind(this);
    this._onResultsResponse = this.onResultsResponse.bind(this);
    this._onHandleKeyDown = this.handleKeyDown.bind(this);
    this._onHandleFocusOut = this.handleFocusOut.bind(this);
    this._onHandleFocusIn = this.handleFocusIn.bind(this);
  }

  connectedCallback() {
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  addEventListeners() {
    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('keydown', this._onHandleKeyDown);
    this.listElement.addEventListener('keydown', this._onHandleKeyDown);
    this.addEventListener('focusout', this._onHandleFocusOut);
    this.addEventListener('focusin', this._onHandleFocusIn);
  }

  removeEventListeners() {
    this.input.removeEventListener('input', this._onInput);
    this.input.removeEventListener('keydown', this._onHandleKeyDown);
    this.listElement.removeEventListener('keydown', this._onHandleKeyDown);
    this.removeEventListener('focusout', this._onHandleFocusOut);
    this.removeEventListener('focusin', this._onHandleFocusIn);
  }

  onInput() {
    this.hasResults = false;

    // Cancel previous fetch requests
    if (this.controller) {
      this.controller.abort();
    }

    if (this.input.value.length < 3) {
      this.resultsClose();
      return;
    }

    if (this.listElement === null) {
      return;
    }

    this.startLoading();

    if (this.searchEndPoint === null) {
      // Return dummy results
      // Add delay to simulate network request
      setTimeout(() => {
        this.onResultsResponse([
          { title: 'cat4', link: '#' },
          { title: 'new group reading test', link: '#' },
          { title: 'transition from primary', link: '#' },
          { title: 'progress test in science', link: '#' },
          { title: 'pass', link: '#' },
        ]);
      }, 500);
    } else {
      // Create a new AbortController instance for the new request
      this.controller = new AbortController();
      const signal = this.controller.signal;

      // Make request and get data, then trigger onResultsResponse
      fetch(this.searchEndPoint + encodeURIComponent(this.input.value), { signal })
        .then(response => response.json())
        .then(data => this.onResultsResponse(data))
        .catch(error => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted');
          } else {
            console.error('Error:', error);
          }
          this.endLoading();
        });
    }
  }

  resultsOpen() {
    this.resultsContainer.removeAttribute('hidden');

    EscapeManager.add(this.escapeManagerID, () => {
      this.resultsClose();
      this.input.focus();
    });
  }
  resultsClose() {
    this.resultsContainer.setAttribute('hidden', '');

    EscapeManager.remove(this.escapeManagerID);
  }

  startLoading() {
    this.setAttribute('data-loading', true);
    this.buttonIcon.classList.remove('fa-magnifying-glass');
    this.buttonIcon.classList.add('fa-spinner');
    this.buttonIcon.classList.add('fa-spin');
  }
  endLoading() {
    this.removeAttribute('data-loading');
    this.buttonIcon.classList.remove('fa-spinner');
    this.buttonIcon.classList.remove('fa-spin');
    this.buttonIcon.classList.add('fa-magnifying-glass');
  }

  onResultsResponse(listData = []) {
    this.endLoading();

    const previousFocusedElement = this.listElement.querySelector('a:focus');
    const previousFocusedHref = previousFocusedElement
      ? previousFocusedElement.getAttribute('href')
      : null;

    this.listElement.innerHTML = '';

    listData.forEach(item => {
      const li = document.createElement('li');
      let element = item.element || 'a';
      const a = document.createElement(element);
      // item.title may be html encoded. Remove html elements
      a.innerHTML = item.title.replace(/<\/?[^>]+(>|$)/g, '');
      if (item.link) a.href = item.link;
      li.appendChild(a);
      this.listElement.appendChild(li);

      if (item.link === previousFocusedHref) {
        a.focus();
      }
    });

    if (listData.length > 0) {
      this.resultsOpen();
      this.hasResults = true;
    } else {
      this.resultsClose();
      this.input.focus();
    }
  }

  handleKeyDown(event) {
    const focusableElements = Array.from(this.listElement.querySelectorAll('a'));
    const currentIndex = focusableElements.indexOf(document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (currentIndex === -1) {
          focusableElements[0]?.focus();
        } else if (currentIndex < focusableElements.length - 1) {
          focusableElements[currentIndex + 1].focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (currentIndex > 0) {
          focusableElements[currentIndex - 1].focus();
        } else if (currentIndex === 0) {
          this.input.focus();
        }
        break;
    }
  }

  handleFocusOut(event) {
    if (!this.contains(event.relatedTarget)) {
      this.resultsClose();
    }
  }
  handleFocusIn(event) {
    if (this.hasResults) {
      this.resultsOpen();
    }
  }
}

customElements.define('search-form', SearchForm);
